import React, { useState } from "react";
import { Check, Clock, Wallet, Target, Users, Award, Calendar, CreditCard, PartyPopper, Camera, Utensils, Mic, Wand2, Cake, Phone, MessageCircle, X, Star, Gift, Lightbulb, AlertTriangle, Eye } from "lucide-react";
import { IMAGES, WHATSAPP_LINK, WHATSAPP_NUMBER } from "../../constants";
import { LogoWatermark } from "./HeroSections";
import { PackageImageSlider } from "./PackageImageSlider";

const PACKAGES_DATA = [
  {
    name: "Simple Surprise", tag: "BASIC", price: "3,999", color: "#25D366", emoji: "🎈",
    tagline: "Chhota sa celebration, dil se",
    image: IMAGES.packages.basic,
    images: [
      "/gallery/basic/birthday/00.jpg",
      "/gallery/basic/birthday/03.jpg",
      "/gallery/basic/anniversary/01.jpg",
      "/gallery/basic/baby_shower/00.jpg",
      "/gallery/basic/others/02.jpg",
      "/gallery/basic/birthday/05.jpg",
    ],
    features: [
      "Balloon bunch decoration (80–100 balloons)",
      "\"Happy Birthday\" banner",
      "Basic table decoration (cake table)",
      "Standard color theme (2 colors)"
    ],
    perfectFor: ["Last-minute birthday plans", "Small family surprises", "Quick, budget-friendly setups"],
    setupTime: "60–90 minutes",
    whyChoose: ["Quick & affordable", "Ideal for intimate celebrations", "No heavy setup required"],
    note: "This is a minimal setup, best for small gatherings (4–8 people)",
    bonus: null
  },
  {
    name: "Family Celebration", tag: "CLASSIC", price: "5,999", color: "#3B82F6", emoji: "🏡",
    tagline: "Ghar ke log, perfect vibe",
    image: IMAGES.packages.classic,
    images: [
      "/gallery/classic/birthday/00.jpg",
      "/gallery/classic/birthday/03.jpg",
      "/gallery/classic/anniversary/01.jpg",
      "/gallery/classic/baby_shower/02.jpg",
      "/gallery/classic/others/01.jpg",
      "/gallery/classic/birthday/05.jpg",
    ],
    features: [
      "Balloon arch / ring decoration (250–300 balloons)",
      "Theme-based color setup (2–3 colors)",
      "Cake table decoration",
      "Basic backdrop setup",
      "\"Happy Birthday\" / \"Happy Anniversary\" foil"
    ],
    perfectFor: ["Family birthdays", "Anniversary celebrations", "Small get-togethers"],
    setupTime: "1.5–2 hours",
    whyChoose: ["Balanced decor + budget", "Proper party feel at home", "Great for 8–15 people"],
    note: null,
    bonus: null
  },
  {
    name: "Birthday Special", tag: "PREMIUM", price: "8,999", color: "#E83E8C", popular: true, emoji: "🎂",
    tagline: "Sabse popular choice",
    image: IMAGES.packages.premium,
    images: [
      "/gallery/premium/birthday/00.jpg",
      "/gallery/premium/birthday/02.jpg",
      "/gallery/premium/anniversary/03.jpg",
      "/gallery/premium/baby_shower/01.jpg",
      "/gallery/premium/anniversary/08.jpg",
      "https://images.unsplash.com/photo-1639316964796-911743e23ff4?w=600&q=80",
    ],
    features: [
      "Theme-based decoration (Cartoon / Elegant / Custom colors)",
      "350–450 Balloons",
      "Balloon garland + backdrop stand",
      "Customized name on backdrop",
      "Cake table premium setup",
      "LED name light / neon (Happy Birthday)",
      "Decorative props (as per theme)"
    ],
    perfectFor: ["Kids' birthday parties", "Themed celebrations", "Customers who want a complete experience"],
    setupTime: "2–2.5 hours",
    whyChoose: ["Most loved & most booked package", "Full party vibe + great photos", "Ideal for 15–25 guests"],
    note: null,
    bonus: "1 surprise element from DoozyGo® 🎁"
  },
  {
    name: "Grand Celebration", tag: "LUXURY", price: "14,999", color: "#DC2626", emoji: "✨",
    tagline: "Jahan sab bole — WOW!",
    image: IMAGES.packages.luxury,
    images: [
      "/gallery/luxury/birthday/00.jpg",
      "/gallery/luxury/birthday/05.jpg",
      "/gallery/luxury/anniversary/03.jpg",
      "/gallery/luxury/baby_shower/01.jpg",
      "/gallery/luxury/birthday/12.jpg",
      "https://images.unsplash.com/photo-1681497397524-0dabeca42094?w=600&q=80",
    ],
    features: [
      "Premium theme decor (high-end finish)",
      "500–600 Balloons",
      "Entry decoration (welcome setup)",
      "Full backdrop with customized name",
      "Advanced balloon styling (organic / designer)",
      "LED lights / fairy lights",
      "Cake table luxury styling",
      "Premium props & elements"
    ],
    perfectFor: ["Big birthdays / milestone events", "Premium home parties", "Customers who want a luxury experience"],
    setupTime: "2.5–3 hours",
    whyChoose: ["Instagram-worthy setup 📸", "Premium look & feel", "Ideal for 20–40 guests"],
    note: null,
    bonus: "1 surprise premium element from DoozyGo® 🎁"
  },
  {
    name: "Build Your Party", tag: "CUSTOM", price: "You Decide", color: "#7C3AED", emoji: "🎨",
    tagline: "Apni party, apne hisaab se!",
    image: "/gallery/basic/others/00.jpg",
    images: [
      "/gallery/basic/others/00.jpg",
      "/gallery/premium/birthday/01.jpg",
      "/gallery/luxury/anniversary/06.jpg",
      "/gallery/classic/baby_shower/05.jpg",
      "/gallery/luxury/birthday/08.jpg",
      "https://images.unsplash.com/photo-1759556893285-dd64e0c2cf44?w=600&q=80",
    ],
    features: [
      "Pick only what YOU want — pay for what you choose",
      "30+ decor, cake, entertainment & experience options",
      "LED boards, balloons, cakes, photographer & more",
      "Pre-made combos from ₹2,499 for quick selection",
      "Custom notes for personalization on each item",
      "Mix & match any category — no restrictions"
    ],
    perfectFor: ["Budget-conscious planners who want specific items", "Unique celebrations with custom elements", "Anyone who knows exactly what they want"],
    setupTime: "Varies by selection",
    whyChoose: ["100% control over your setup", "No unnecessary items — no overpaying", "Best for specific requests"],
    note: "Minimum order value: ₹999. Setup time depends on items selected.",
    bonus: null,
    isCustom: true
  }
];

const PackageDetailModal = ({ pkg, onClose }) => {
  if (!pkg) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4" onClick={onClose} data-testid="package-detail-modal">
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="relative">
          <div className="aspect-[16/9] overflow-hidden rounded-t-3xl">
            <PackageImageSlider images={pkg.images} name={`modal-${pkg.tag.toLowerCase()}`} interval={4000} />
          </div>
          <button onClick={onClose} className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-md hover:bg-white transition-all" data-testid="package-modal-close"><X size={20} className="text-[#1E1B4B]" /></button>
          {pkg.popular && (
            <div className="absolute top-3 left-3"><span className="bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg">MOST POPULAR</span></div>
          )}
        </div>

        <div className="p-6">
          <div className="flex items-center justify-between mb-1">
            <div className="inline-block px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: `${pkg.color}15`, color: pkg.color }}>{pkg.tag}</div>
            <span className="text-2xl">{pkg.emoji}</span>
          </div>
          <h3 className="font-['Outfit'] text-2xl font-bold text-[#1E1B4B] mt-2">{pkg.name} – ₹{pkg.price}</h3>
          <p className="text-[#475569] italic text-sm mt-1">"{pkg.tagline}"</p>

          {/* Perfect For */}
          <div className="mt-5">
            <h4 className="flex items-center gap-2 font-semibold text-[#1E1B4B] text-sm mb-2"><Target size={16} className="text-[#E83E8C]" /> Perfect For:</h4>
            <ul className="space-y-1.5">
              {pkg.perfectFor.map((item) => <li key={item} className="flex items-start gap-2 text-[#475569] text-sm"><Check size={14} className="text-[#25D366] mt-0.5 flex-shrink-0" />{item}</li>)}
            </ul>
          </div>

          {/* What You Get */}
          <div className="mt-5">
            <h4 className="flex items-center gap-2 font-semibold text-[#1E1B4B] text-sm mb-2"><Gift size={16} className="text-[#E83E8C]" /> What You Get:</h4>
            <ul className="space-y-1.5">
              {pkg.features.map((feature) => <li key={feature} className="flex items-start gap-2 text-[#475569] text-sm"><Check size={14} className="text-[#25D366] mt-0.5 flex-shrink-0" />{feature}</li>)}
            </ul>
          </div>

          {/* Setup Time */}
          <div className="mt-5 bg-[#FFFAF5] rounded-xl p-3">
            <p className="flex items-center gap-2 text-sm text-[#475569]"><Clock size={14} className="text-[#FF9F1C]" /> <span className="font-semibold">Setup Time:</span> Approx. {pkg.setupTime}</p>
          </div>

          {/* Why Choose */}
          <div className="mt-5">
            <h4 className="flex items-center gap-2 font-semibold text-[#1E1B4B] text-sm mb-2"><Lightbulb size={16} className="text-[#FF9F1C]" /> Why Choose This:</h4>
            <ul className="space-y-1.5">
              {pkg.whyChoose.map((item) => <li key={item} className="flex items-start gap-2 text-[#475569] text-sm"><Star size={14} className="text-[#FFD166] mt-0.5 flex-shrink-0" />{item}</li>)}
            </ul>
          </div>

          {/* Bonus */}
          {pkg.bonus && (
            <div className="mt-4 bg-gradient-to-r from-[#E83E8C]/10 to-[#FF9F1C]/10 rounded-xl p-3">
              <p className="flex items-center gap-2 text-sm font-semibold text-[#E83E8C]"><Gift size={14} /> Bonus: {pkg.bonus}</p>
            </div>
          )}

          {/* Note */}
          {pkg.note && (
            <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-xl p-3">
              <p className="flex items-start gap-2 text-xs text-yellow-700"><AlertTriangle size={14} className="flex-shrink-0 mt-0.5" /> {pkg.note}</p>
            </div>
          )}

          {/* CTA */}
          <a href="#book-online" onClick={(e) => { onClose(); window.dispatchEvent(new CustomEvent('selectPackage', { detail: pkg.tag })); }} className="block w-full text-center py-3.5 rounded-full font-bold text-white mt-6 transition-all hover:scale-105 shadow-lg" style={{ backgroundColor: pkg.color }} data-testid="package-modal-book">Book {pkg.name} Now</a>
        </div>
      </div>
    </div>
  );
};

export const PackagesSection = () => {
  const [selectedPkg, setSelectedPkg] = useState(null);

  return (
    <section id="packages" className="py-20 md:py-28 bg-[#FFFAF5]" data-testid="packages-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-[#1E1B4B] mb-4">Simple Packages. No Confusion.</h2>
          <div className="section-divider"></div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6 overflow-visible">
          {PACKAGES_DATA.map((pkg) => (
            <div key={pkg.tag} className={`bg-white rounded-3xl overflow-visible card-shadow card-shadow-hover flex flex-col h-full ${pkg.popular ? 'ring-2 ring-[#E83E8C] relative mt-4' : ''}`} data-testid={`package-card-${pkg.tag.toLowerCase()}`}>
              {pkg.popular && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                  <span className="bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white text-xs font-bold px-4 py-1.5 rounded-full whitespace-nowrap shadow-lg">MOST POPULAR</span>
                </div>
              )}
              <div className="aspect-[4/3] overflow-hidden rounded-t-3xl">
                <PackageImageSlider images={pkg.images} name={pkg.tag.toLowerCase()} />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-center justify-between mb-1">
                  <div className="inline-block px-3 py-1 rounded-full text-sm font-bold" style={{ backgroundColor: `${pkg.color}15`, color: pkg.color }}>{pkg.tag}</div>
                  <span className="text-lg">{pkg.emoji}</span>
                </div>
                <h3 className="font-['Outfit'] text-lg font-bold text-[#1E1B4B] mt-1">{pkg.name}</h3>
                <p className="text-xs text-[#475569] italic mb-3">"{pkg.tagline}"</p>
                <div className="flex items-baseline gap-1 mb-4"><span className="text-3xl font-bold text-[#1E1B4B]">₹{pkg.price}</span></div>
                <ul className="space-y-2 mb-4 flex-1">
                  {pkg.features.slice(0, 4).map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-[#475569] text-sm"><Check size={16} className="text-[#25D366] mt-0.5 flex-shrink-0" /><span>{feature}</span></li>
                  ))}
                  {pkg.features.length > 4 && (
                    <li className="text-[#E83E8C] text-sm font-medium">+ {pkg.features.length - 4} more included</li>
                  )}
                </ul>
                {pkg.bonus && (
                  <div className="bg-gradient-to-r from-[#E83E8C]/10 to-[#FF9F1C]/10 rounded-lg p-2 mb-4">
                    <p className="text-xs font-semibold text-[#E83E8C] flex items-center gap-1"><Gift size={12} /> {pkg.bonus}</p>
                  </div>
                )}
                <div className="bg-[#FFFAF5] rounded-xl p-2.5 mb-4">
                  <p className="text-xs text-[#475569] flex items-center gap-1"><Clock size={12} className="text-[#FF9F1C]" /> Setup: {pkg.setupTime}</p>
                </div>
                <button onClick={() => setSelectedPkg(pkg)} className="w-full text-center py-2.5 rounded-full font-semibold text-sm transition-all hover:scale-105 mb-3 flex items-center justify-center gap-2 border-2" style={{ color: pkg.color, borderColor: pkg.color }} data-testid={`package-details-${pkg.tag.toLowerCase()}`}>
                  <Eye size={16} /> Check Details
                </button>
                <a href="#book-online" onClick={() => window.dispatchEvent(new CustomEvent('selectPackage', { detail: pkg.tag }))} className="block w-full text-center py-3 rounded-full font-semibold transition-all hover:scale-105" style={{ backgroundColor: pkg.popular ? pkg.color : pkg.color, color: 'white' }} data-testid={`package-cta-${pkg.tag.toLowerCase()}`}>{pkg.isCustom ? "Build & Book" : "Book Now"}</a>
              </div>
            </div>
          ))}
        </div>
      </div>
      <PackageDetailModal pkg={selectedPkg} onClose={() => setSelectedPkg(null)} />
    </section>
  );
};

export const AddOnsSection = () => {
  const addons = [
    { icon: Cake, name: "Cake", price: "₹1,000 onwards", image: IMAGES.addons.cake },
    { icon: Camera, name: "Photographer", price: "₹2,500 onwards", image: IMAGES.addons.photographer },
    { icon: Utensils, name: "Catering", price: "₹400/plate onwards", image: IMAGES.addons.catering },
    { icon: Mic, name: "Anchor / Game Host", price: "On request", image: "https://images.unsplash.com/photo-1574593749297-cb33a69cd8d4?w=300&q=80" },
    { icon: Wand2, name: "Magician / Kids Entertainment", price: "On request", image: "https://images.pexels.com/photos/5560039/pexels-photo-5560039.jpeg?w=300" }
  ];

  return (
    <section id="addons" className="py-20 md:py-28 bg-white relative overflow-hidden" data-testid="addons-section">
      <LogoWatermark />
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-[#1E1B4B] mb-4">Make it a Complete Party</h2>
          <div className="section-divider"></div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {addons.map((addon) => (
            <div key={addon.name} className="addon-card bg-[#FFFAF5] rounded-2xl p-6 text-center transition-all hover:shadow-lg cursor-pointer" data-testid={`addon-card-${addon.name}`}>
              {addon.image ? (
                <div className="w-20 h-20 mx-auto mb-4 rounded-full overflow-hidden"><img src={addon.image} alt={addon.name} className="w-full h-full object-cover" /></div>
              ) : (
                <div className="feature-icon mx-auto mb-4"><addon.icon size={28} className="text-[#E83E8C]" /></div>
              )}
              <h3 className="font-semibold text-[#1E1B4B] mb-2">{addon.name}</h3>
              <p className="text-sm text-[#FF9F1C] font-medium">{addon.price}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const WhyDoozyGoSection = () => {
  const benefits = [
    { icon: Clock, title: "Setup in just 2 hours", description: "Quick & efficient team" },
    { icon: Wallet, title: "Budget-friendly packages", description: "Starting at just ₹3,999" },
    { icon: Target, title: "End-to-end service", description: "We handle everything" },
    { icon: Users, title: "Backed by Big Kreation Entertainment", description: "Experienced professionals" },
    { icon: Award, title: "Trusted by 500+ Happy Families", description: "Happy celebrations" }
  ];

  return (
    <section className="py-20 md:py-28 bg-[#1E1B4B] relative overflow-hidden" data-testid="why-doozygo-section">
      <div className="absolute top-0 right-0 w-64 h-64 bg-[#E83E8C] rounded-full opacity-10 -translate-y-1/2 translate-x-1/2"></div>
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#FF9F1C] rounded-full opacity-10 translate-y-1/2 -translate-x-1/2"></div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-white mb-4">Why Choose DoozyGo®?</h2>
          <div className="section-divider"></div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {benefits.map((benefit) => (
            <div key={benefit.title} className="text-center p-6 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 transition-all hover:bg-white/10" data-testid={`benefit-card-${benefit.title}`}>
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#E83E8C] to-[#FF9F1C] flex items-center justify-center"><benefit.icon size={28} className="text-white" /></div>
              <h3 className="font-semibold text-white mb-2">{benefit.title}</h3>
              <p className="text-sm text-white/70">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const HowItWorksSection = () => {
  const steps = [
    { number: "1", title: "Choose your date & package", description: "Select from our calendar and pick a package", icon: Calendar },
    { number: "2", title: "Pay securely online", description: "Pay advance or full amount with UPI/Cards", icon: CreditCard },
    { number: "3", title: "Enjoy your party", description: "Sit back & let us handle everything", icon: PartyPopper }
  ];

  return (
    <section id="how-it-works" className="py-20 md:py-28 bg-white relative overflow-hidden" data-testid="how-it-works-section">
      <LogoWatermark />
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-[#1E1B4B] mb-4">Book Your Party in 3 Simple Steps</h2>
          <div className="section-divider"></div>
        </div>
        <div className="grid md:grid-cols-3 gap-8 relative">
          <div className="hidden md:block absolute top-16 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-[#E83E8C] via-[#FF9F1C] to-[#FFD166]"></div>
          {steps.map((step) => (
            <div key={step.number} className="text-center relative" data-testid={`step-card-${step.number}`}>
              <div className="step-number w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#E83E8C] to-[#FF9F1C] flex items-center justify-center text-white text-2xl font-bold shadow-lg">{step.number}</div>
              <div className="feature-icon mx-auto mb-4"><step.icon size={28} className="text-[#E83E8C]" /></div>
              <h3 className="text-xl font-bold text-[#1E1B4B] mb-2">{step.title}</h3>
              <p className="text-[#475569]">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const UrgencySection = () => {
  return (
    <section className="py-16 md:py-20 bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C]" data-testid="urgency-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="urgency-badge inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full mb-6"><Clock size={18} /><span className="font-semibold">Limited Slots Available This Weekend</span></div>
        <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-white mb-4">Don't miss your preferred date — we book fast!</h2>
        <a href="#book-online" className="inline-flex items-center gap-2 bg-white text-[#E83E8C] px-8 py-4 rounded-full font-bold text-lg transition-all hover:scale-105 shadow-lg mt-6" data-testid="urgency-cta"><CreditCard size={22} /> Book Online Now</a>
      </div>
    </section>
  );
};

export const FinalCTASection = () => {
  return (
    <section className="py-20 md:py-28 bg-[#FFFAF5]" data-testid="final-cta-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <PartyPopper size={48} className="mx-auto text-[#E83E8C] mb-6" />
        <h2 className="font-['Outfit'] text-3xl md:text-5xl font-bold text-[#1E1B4B] mb-6">Let's Make Your Celebration Special</h2>
        <p className="text-xl text-[#475569] mb-8">No stress. No hassle. Just pure celebration.</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a href="#book-online" className="inline-flex items-center justify-center gap-3 bg-[#E83E8C] hover:bg-[#d62f7a] text-white px-10 py-5 rounded-full font-bold text-xl transition-all hover:scale-105 shadow-xl btn-shine" data-testid="final-cta-online"><CreditCard size={26} /> Book Online Now</a>
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1da851] text-white px-10 py-5 rounded-full font-bold text-xl transition-all hover:scale-105 shadow-xl btn-shine" data-testid="final-cta-btn"><MessageCircle size={26} /> Chat on WhatsApp</a>
        </div>
      </div>
    </section>
  );
};
